import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { delay, map, Observable, of, switchMap } from 'rxjs';
import { environment } from '../../environments/environment';
import { SessionService } from './session.service';

export interface TranslationFile {
    fileName: string;
    base64File?: string | null;
};

export interface UploadTranslationFile {
    fileName: string;
    base64File: string;
}

export interface UploadTranslationRequest {
    originalFile: UploadTranslationFile;
    sampleDataFiles: UploadTranslationFile[];
    expectedOutputFile: UploadTranslationFile | null;
    friendlyName: string;
    submitterWillValidate: boolean;
}

export interface UploadTranslationResponse {
    guidId?: string | null;
    warnings?: string[] | null;
    invalidReasons?: string[] | null;
    responseTypeId?: number | null;
    responseTypeDescription?: string | null;
}

export interface TranslationApiItem {
    id: number;
    submissionId: string;
    friendlyName?: string | null;
    originalFile: TranslationFile;
    translatedFile: TranslationFile;
    expectedOutputFile: TranslationFile;
    sampleDataFiles?: TranslationFile[] | null;
    isCompleted: boolean;
    translationFailed: boolean;
    submitterWillValidate: boolean;
    statusMessage?: string | null;
}

type TranslationsApiResponse =
    | TranslationApiItem[]
    | {
        codeTranslations?: TranslationApiItem[];
    };

const mockTranslations: TranslationApiItem[] = [
    {
        id: 1,
        submissionId: '0f7f5d2a-ef9a-4a43-a7ce-cb1f3bd3f1c1',
        friendlyName: 'Annual Report',
        originalFile: {
            fileName: 'annual_report.sas',
            base64File: null,
        },
        sampleDataFiles: [{
            fileName: 'ar_sample.sas7bdat',
            base64File: null
        }, {
            fileName: 'ar_sample_2.sas7bdat',
            base64File: null
        }],
        expectedOutputFile: {
            fileName: 'ar_expected_output.csv',
            base64File: null
        },
        translatedFile: {
            fileName: 'annual_report.r',
            base64File: null
        },
        isCompleted: false,
        translationFailed: false,
        submitterWillValidate: false
    },
    {
        id: 2,
        submissionId: '5b54120f-c56c-47fd-a167-5c734f9b6f2d',
        friendlyName: 'Monthly Report',
        originalFile: {
            fileName: 'monthly_report.sas',
            base64File: null,
        },
        sampleDataFiles: [{
            fileName: 'mr_sample.sas7bdat',
            base64File: null
        }, {
            fileName: 'mr_sample_2.sas7bdat',
            base64File: null
        }],
        expectedOutputFile: {
            fileName: 'mr_expected_output.csv',
            base64File: null
        },
        translatedFile: {
            fileName: 'monthly_report.r',
            base64File: null
        },
        isCompleted: false,
        translationFailed: true,
        submitterWillValidate: false
    },
    {
        id: 3,
        submissionId: '456d5856-af2e-49bc-bc22-78ac7971c754',
        friendlyName: 'Quarterly Report',
        originalFile: {
            fileName: 'quarterly_report.sas',
            base64File: null,
        },
        sampleDataFiles: [{
            fileName: 'qr_sample.sas7bdat',
            base64File: null
        }, {
            fileName: 'qr_sample_2.sas7bdat',
            base64File: null
        }],
        expectedOutputFile: {
            fileName: 'qr_expected_output.csv',
            base64File: null
        },
        translatedFile: {
            fileName: 'quarterly_report.r',
            base64File: null
        },
        isCompleted: true,
        translationFailed: false,
        submitterWillValidate: false
    },
    {
        id: 4,
        submissionId: '5b2f8f63-ae52-4943-bd2b-d0d3d9f9c6f0',
        friendlyName: 'Weekly Report',
        originalFile: {
            fileName: 'weekly_report.sas',
            base64File: null,
        },
        sampleDataFiles: [{
            fileName: 'wr_sample.sas7bdat',
            base64File: null
        }, {
            fileName: 'wr_sample_2.sas7bdat',
            base64File: null
        }],
        expectedOutputFile: {
            fileName: 'wr_expected_output.csv',
            base64File: null
        },
        translatedFile: {
            fileName: 'weekly_report.r',
            base64File: null
        },
        isCompleted: false,
        translationFailed: false,
        submitterWillValidate: true
    },
];

@Injectable({
    providedIn: 'root',
})
export class TranslationsService {
    private readonly translationsUrl = this.buildApiUrl('CodeTranslation/getTranslations');
    private readonly uploadUrl = this.buildApiUrl('CodeTranslation/submitTranslation');
    private readonly downloadUrl = this.buildApiUrl('CodeTranslation/downloadTranslatedFile');
    private readonly deleteUrl = this.buildApiUrl('CodeTranslation/deleteTranslation');

    constructor(
        private readonly http: HttpClient,
        private readonly sessionService: SessionService
    ) { }

    getTranslations(): Observable<TranslationApiItem[]> {
        if (environment.useMockTranslations) {
            return of(mockTranslations).pipe(delay(300));
        }

        return this.sessionService.getAccessToken().pipe(
            switchMap((accessToken) =>
                this.http.get<TranslationsApiResponse>(this.translationsUrl, {
                    headers: {
                        Authorization: `Bearer ${accessToken}`
                    }
                })
            ),
            map((response) => this.unwrapTranslations(response))
        );
    }

    uploadTranslation(uploadRequest: UploadTranslationRequest): Observable<UploadTranslationResponse> {
        if (environment.useMockTranslations) {
            return of({
                guidId: crypto.randomUUID(),
                warnings: [],
                invalidReasons: [],
                responseTypeId: 100,
                responseTypeDescription: 'Success'
            }).pipe(delay(300));
        }

        return this.sessionService.getAccessToken().pipe(
            switchMap((accessToken) =>
                this.http.post<UploadTranslationResponse>(this.uploadUrl, uploadRequest, {
                    headers: {
                        Authorization: `Bearer ${accessToken}`
                    }
                })
            )
        );
    }

    downloadTranslatedFile(submissionId: string, fileName: string): Observable<Blob> {
        if (environment.useMockTranslations) {
            return of(new Blob([`Mock translated file: ${fileName}\n`], { type: 'text/plain' }))
                .pipe(delay(300));
        }

        return this.sessionService.getAccessToken().pipe(
            switchMap((accessToken) =>
                this.http.get(this.downloadUrl, {
                    headers: {
                        Authorization: `Bearer ${accessToken}`
                    },
                    params: {
                        submissionId,
                        fileName
                    },
                    responseType: 'blob'
                })
            )
        );
    }

    deleteTranslation(submissionId: string): Observable<void> {
        if (environment.useMockTranslations) {
            return of(void 0).pipe(delay(300));
        }

        return this.sessionService.getAccessToken().pipe(
            switchMap((accessToken) =>
                this.http.delete<void>(this.deleteUrl, {
                    headers: {
                        Authorization: `Bearer ${accessToken}`
                    },
                    params: {
                        submissionId
                    }
                })
            )
        );
    }

    private buildApiUrl(path: string): string {
        const baseUrl = environment.apiBaseUrl.replace(/\/+$/, '');
        return baseUrl ? `${baseUrl}/${path}` : `/${path}`;
    }

    private unwrapTranslations(response: TranslationsApiResponse): TranslationApiItem[] {
        if (Array.isArray(response)) return response;

        return response.codeTranslations ?? [];
    }
}
